import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const debugLog = (...args: unknown[]) => {
  if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
    // eslint-disable-next-line no-console
    console.debug('[SessionContext]', ...args);
  }
};

import type { SessionProfile } from '@/types';
import { clearSession, getSession, reauthenticate, login as startLogin, logout as performLogout } from '@/lib/session';
import { PORTAL_SESSION_EXPIRED_EVENT } from '@/lib/session-expiry';

interface SessionState {
  session: SessionProfile | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  login: (returnUrl?: string, options?: { register?: boolean }) => void;
}

const SessionContext = createContext<SessionState | undefined>(undefined);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SessionProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<SessionProfile | null>(null);
  const expiryRedirectingRef = useRef(false);

  const loadSession = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      debugLog('Fetching session');
      const profile = await getSession();
      debugLog('Session fetched', profile);
      sessionRef.current = profile;
      setSession(profile);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load session';
      console.error('[SessionContext] error', message, err);
      setError(message);
      sessionRef.current = null;
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    debugLog('Initializing session context');
    void loadSession();
  }, [loadSession]);

  useEffect(() => {
    const handleSessionExpired = () => {
      if (!sessionRef.current || expiryRedirectingRef.current) return;
      expiryRedirectingRef.current = true;
      debugLog('Session expired; restarting login');
      clearSession();
      sessionRef.current = null;
      setSession(null);
      setError(null);
      setLoading(true);
      void reauthenticate(window.location.href).catch(() => {
        // Render the tenant-aware login page for retry; never choose platform login here.
        expiryRedirectingRef.current = false;
        setLoading(false);
      });
    };

    window.addEventListener(PORTAL_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(PORTAL_SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const refresh = useCallback(async () => {
    debugLog('Refreshing session');
    clearSession();
    await loadSession();
  }, [loadSession]);

  const handleLogout = useCallback(async () => {
    debugLog('Logging out');
    await performLogout();
  }, []);

  const contextValue = useMemo<SessionState>(
    () => ({
      session,
      loading,
      error,
      refresh,
      logout: handleLogout,
      login: (returnUrl?: string, options?: { register?: boolean }) => {
        debugLog('Initiating login', { returnUrl, options });
        startLogin(returnUrl, options);
      }
    }),
    [session, loading, error, refresh, handleLogout]
  );

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
